import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUserData } from "context/UserDataContext";
import { useLanguage } from "context/LanguageContext";

import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DetailedStatisticsCard from "examples/Cards/StatisticsCards/DetailedStatisticsCard";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardSkeleton from "components/SkeletonLoader/DashboardSkeleton";
import { formatTotalesPorMoneda, sumarPorMoneda, promediosPorMoneda, monedaRetiro } from "utils/formatImporteRetiro";

/** Día del mes (1–31) desde fecha ISO para evitar desfases por zona horaria. */
function calendarDayFromFecha(fecha) {
  if (!fecha) return null;
  const str = typeof fecha === "string" ? fecha : new Date(fecha).toISOString();
  const ymd = str.includes("T") ? str.split("T")[0] : str.slice(0, 10);
  const parts = ymd.split("-");
  if (parts.length >= 3) {
    const d = parseInt(parts[2], 10);
    if (!Number.isNaN(d)) return d;
  }
  return new Date(fecha).getUTCDate();
}

function Default() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { 
    userData, 
    empresaData,
    usuariosEmpresa, 
    fetchRetiros,
    userDataLoading,
    empresaDataLoading,
    usuariosEmpresaLoading 
  } = useUserData();
  const { t, lang } = useLanguage();
  const [activeUsers, setActiveUsers] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState("");
  const [withdrawalCount, setWithdrawalCount] = useState(0);
  const [averageWithdrawal, setAverageWithdrawal] = useState("");
  const [chartData, setChartData] = useState(null);
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [nombreUsuario, setNombreUsuario] = useState('');
  const [saludoBienvenida, setSaludoBienvenida] = useState('Bienvenido');
  const [initialDashboardReady, setInitialDashboardReady] = useState(false);

  const handleMesChange = (event) => {
    setMesSeleccionado(event.target.value);
  };

  const handleAnioChange = (event) => {
    setAnioSeleccionado(event.target.value);
  };

  // Función para obtener y formatear el primer nombre
  const obtenerPrimerNombre = (userData) => {
    if (!userData) return 'Usuario';
    
    // Si existe primerNombre, usarlo
    if (userData.primerNombre) {
      const nombre = userData.primerNombre.trim();
      return nombre.charAt(0).toUpperCase() + nombre.slice(1).toLowerCase();
    }
    
    // Si no existe primerNombre, extraerlo del campo nombre
    if (userData.nombre) {
      const nombreCompleto = userData.nombre.trim();
      const primerNombre = nombreCompleto.split(' ')[0];
      return primerNombre.charAt(0).toUpperCase() + primerNombre.slice(1).toLowerCase();
    }
    
    return 'Usuario';
  };

  // Obtener fecha actual para validación
  const fechaActual = new Date();
  const mesActual = fechaActual.getMonth() + 1;
  const anioActual = fechaActual.getFullYear();

  // Función para verificar si una fecha es válida (no posterior a la actual)
  const esFechaValida = (mes, anio) => {
    if (anio < anioActual) return true;
    if (anio > anioActual) return false;
    return mes <= mesActual;
  };

  // Protección de autenticación: redirigir si no hay usuario
  useEffect(() => {
    // Si Firebase está cargando, esperar
    if (loading) {
      return;
    }

    // Si no hay usuario autenticado, redirigir al login inmediatamente
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }
  }, [navigate, user, loading]);

  useEffect(() => {
    setInitialDashboardReady(false);
  }, [user?.uid]);

  useEffect(() => {
    if (loading || userDataLoading || !user || !userData) {
      return;
    }

    setNombreUsuario(obtenerPrimerNombre(userData));
    setSaludoBienvenida(
      userData.genero === 'F' ? t('dashboard.welcome.female') : t('dashboard.welcome.male')
    );
  }, [user, loading, userDataLoading, userData, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || userDataLoading || empresaDataLoading || usuariosEmpresaLoading) {
      return;
    }

    if (!user) {
      return;
    }

    if (!userData || !usuariosEmpresa) {
      setInitialDashboardReady(true);
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      const empresaId = empresaData?.id || userData?.empresaId;

      if (!empresaId) {
        console.error('❌ [Dashboard] No se encontró empresaId en el contexto');
        if (isMounted) {
          setInitialDashboardReady(true);
        }
        return;
      }

      try {
        const withdrawals = await fetchRetiros(empresaId, mesSeleccionado, anioSeleccionado);

        if (isMounted) {
          const usuariosActivos = usuariosEmpresa.filter((u) => u.activo).length;
          setActiveUsers(usuariosActivos);

          const empresaUserIds = new Set(
            (usuariosEmpresa || [])
              .filter((u) => u?.id && u.nombre && u.apellido && u.documento)
              .map((u) => u.id)
          );

          const retirosFiltrados = withdrawals.filter((retiro) => {
            if (retiro.suscripcion !== false) return false;
            const uid = retiro.userId;
            if (!uid) return false;
            return empresaUserIds.has(uid);
          });

          const totalesPorMoneda = sumarPorMoneda(retirosFiltrados, {
            cantidadKey: "cantidad",
            getMoneda: monedaRetiro,
          });
          const totalesRedondeados = Object.fromEntries(
            Object.entries(totalesPorMoneda).map(([moneda, total]) => [moneda, Math.ceil(total)])
          );
          const count = retirosFiltrados.length;
          const promedios = promediosPorMoneda(retirosFiltrados, {
            cantidadKey: "cantidad",
            getMoneda: monedaRetiro,
          });

          setWithdrawalCount(count);
          setTotalWithdrawn(formatTotalesPorMoneda(totalesRedondeados));
          setAverageWithdrawal(formatTotalesPorMoneda(promedios));

          const diasEnMes = new Date(anioSeleccionado, mesSeleccionado, 0).getDate();

          const withdrawalCountByDay = retirosFiltrados.reduce((acc, retiro) => {
            const day = calendarDayFromFecha(retiro.fecha);
            if (!day) return acc;
            acc[day] = (acc[day] || 0) + 1;
            return acc;
          }, {});

          const labels = [];
          const data = [];

          for (let dia = 1; dia <= diasEnMes; dia++) {
            labels.push(`${t('day')} ${dia}`);
            data.push(withdrawalCountByDay[dia] || 0);
          }

          const nextChartData = {
            labels,
            datasets: [
              {
                label: "",
                data,
                color: "info",
                variant: "withdrawalCounts",
              },
            ],
          };

          setChartData(nextChartData);
        }
      } catch (error) {
        console.error('❌ [Dashboard] ===== ERROR EN FETCH DATA =====');
        console.error('❌ [Dashboard] Tipo de error:', error.constructor.name);
        console.error('❌ [Dashboard] Mensaje:', error.message);
        console.error('❌ [Dashboard] Stack:', error.stack);
        console.error('❌ [Dashboard] ===== FIN DE ERROR =====');
      } finally {
        if (isMounted) {
          setInitialDashboardReady(true);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [mesSeleccionado, anioSeleccionado, user, loading, userData, empresaData, usuariosEmpresa, userDataLoading, empresaDataLoading, usuariosEmpresaLoading, fetchRetiros]);
  






    

  




  const nombresMeses = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => t(`month.${n}`));

  // Generar array de años dinámicamente (desde 2020 hasta el año actual + 1)
  const anioInicio = 2020;
  const anioFin = anioActual + 1; // Incluir el año actual y uno más
  const aniosDisponibles = [];
  for (let anio = anioInicio; anio <= anioFin; anio++) {
    aniosDisponibles.push(anio);
  }

  // Determinar si la fecha seleccionada es la actual
  const esFechaActual = mesSeleccionado === mesActual && anioSeleccionado === anioActual;
  const mesNombre = nombresMeses[mesSeleccionado - 1];

  if (!loading && !user) {
    return null;
  }

  const showDashboardSkeleton =
    loading ||
    (Boolean(user) &&
      (userDataLoading || empresaDataLoading || usuariosEmpresaLoading || !initialDashboardReady));

  return (
    <DashboardLayout>
      {showDashboardSkeleton ? (
        <DashboardSkeleton />
      ) : (
        <>
      <ArgonBox py={1} sx={{ overflowX: 'hidden', boxSizing: 'border-box', maxWidth: '100vw' }}>
        <ArgonBox mb={4} position="relative" zIndex={1}>
          <ArgonTypography variant="h3" fontWeight="bold" color="white" sx={{ zIndex: 2 }}>
            {saludoBienvenida}, {nombreUsuario}
          </ArgonTypography>
          <ArgonTypography variant="h6" color="white" sx={{ zIndex: 2, opacity: 0.8, mt: 1 }}>
            {t('dashboard.subtitle')}
          </ArgonTypography>
        </ArgonBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              title={t('dashboard.activeUsers')}
              count={activeUsers.toString()}
              icon={{ color: "error", component: <i className="ni ni-circle-08" /> }}
              titleColor="black"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              title={
                esFechaActual
                  ? t('dashboard.withdrawnThisMonth')
                  : t('dashboard.withdrawnIn', { month: mesNombre.toLowerCase() })
              }
              count={totalWithdrawn}
              icon={{ color: "success", component: <i className="ni ni-money-coins" /> }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              title={t('dashboard.withdrawalCount')}
              count={withdrawalCount.toString()}
              icon={{ color: "primary", component: <i className="ni ni-bullet-list-67" /> }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DetailedStatisticsCard
              title={t('dashboard.avgWithdrawal')}
              count={averageWithdrawal}
              icon={{ color: "warning", component: <i className="ni ni-chart-bar-32" /> }}
            />
          </Grid>
        </Grid>
      </ArgonBox>
      <ArgonBox>
        {chartData && (
          <Grid item xs={12} lg={10}>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2} />
            <ArgonBox position="relative">
              {/* Selectores flotando en la esquina superior derecha */}
              <ArgonBox
                position="absolute"
                top={16}
                right={16}
                zIndex={2}
                display="flex"
                gap={2}
               
                p={1}
              >
                <Select
                  value={mesSeleccionado}
                  onChange={handleMesChange}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  {nombresMeses.map((mes, index) => {
                    const mesNumero = index + 1;
                    const esValido = esFechaValida(mesNumero, anioSeleccionado);
                    return (
                      <MenuItem 
                        key={mesNumero} 
                        value={mesNumero}
                        disabled={!esValido}
                      >
                        {mes}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Select
                  value={anioSeleccionado}
                  onChange={handleAnioChange}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 100 }}
                  renderValue={(value) => value || ''}
                >
                  {aniosDisponibles.map((anio) => {
                    const esValido = anio <= anioActual;
                    return (
                      <MenuItem 
                        key={anio} 
                        value={anio}
                        disabled={!esValido}
                      >
                        {anio}
                      </MenuItem>
                    );
                  })}
                </Select>
              </ArgonBox>
              <GradientLineChart
                title={t('dashboard.chartTitle', { month: mesNombre, year: anioSeleccionado })}
                chart={chartData}
              />
            </ArgonBox>
          </Grid>
        )}
      </ArgonBox>
        </>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
